import { SetupInstance } from '../../app/msal-account-helpers';
import { protectedResources } from '../../authConfig';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { useLocal } from './../environment';
import { hydrateObjects, cleanUpJsonForTransport } from '../json-helper';
import polly from 'polly-js';
import { AccessTokenHelper } from '../access-token-helper';
import ObjectID from 'bson-objectid';
import { generateObjectId } from '../../utils/idUtils';
import { msalConfig } from '../../app';

export class TenantApi {
    constructor(instance, inProgress, accounts, forceRefresh) {

        this.instance = SetupInstance(instance);
        this.inProgress = inProgress;
        this.account = accounts[0];

        this.accessTokenRequest = {
            scopes: [
                ...protectedResources.apiTenants.scopes.readWrite
            ],
            authority: `https://login.microsoftonline.com/${msalConfig.auth.tenantId}/`,
            forceRefresh: forceRefresh,
            account: this.account,
        };

        this.baseUrl = useLocal ?
            "https://property.manager.internal.gateway/tenants/api/tenants" :
            "https://apim.ianrobertson.co.uk/api/tenants/v1";


        this.accessTokenService = new AccessTokenHelper();
    }

    static tenants = []; // in-memory cache only (no local/session storage)

    #getTenantsFromCache = () => {
        return TenantApi.tenants;
    }

    #getTenantFromCache = id => {
        const tenants = this.#getTenantsFromCache();
        return tenants.find(t => t.id === id);
    }

    #addTenantToCache = tenant => {
        const tenants = this.#getTenantsFromCache();
        const existingTenantIndex = tenants.findIndex(t => t.id === tenant.id);
        // Ensure each tenancy has a client-side id for UI references when server ids are absent
        if (Array.isArray(tenant.tenancies)) {
            tenant.tenancies.forEach(t => {
                if (!t.clientId) {
                    t.clientId = generateObjectId();
                }
            });
        }
        if (existingTenantIndex === -1) {
            tenants.push(tenant);
        } else {
            tenants.splice(existingTenantIndex, 1, tenant);
        }

        // Persist only in-memory; a full page refresh will clear this cache
        TenantApi.tenants = tenants;
    }

    #addTenantsToCache = tenants => {
        for (const tenant of tenants) {
            // Ensure tenancies have clientIds before adding to cache
            if (Array.isArray(tenant.tenancies)) {
                tenant.tenancies.forEach(t => {
                    if (!t.clientId) t.clientId = generateObjectId();
                });
            }
            this.#addTenantToCache(tenant);
        }
    }

    static clearCache = () => {
        TenantApi.tenants = [];
    };

    // Helper: return a deep clone of tenant with client-only fields removed
    _stripClientIds = t => {
        try {
            // Deep clone via JSON to avoid carrying references to File objects
            const clone = JSON.parse(JSON.stringify(t));

            // Walk object recursively to remove any client-only or binary fields
            const walk = (obj) => {
                if (!obj || typeof obj !== 'object') return;
                if (Array.isArray(obj)) {
                    for (const item of obj) walk(item);
                    return;
                }

                for (const key of Object.keys(obj)) {
                    try {
                        const val = obj[key];
                        // Remove client-side ids
                        if (key === 'clientId') {
                            delete obj[key];
                            continue;
                        }

                        // Remove uploaded File references or any binary container
                        if (key === 'file') {
                            delete obj[key];
                            continue;
                        }

                        // Remove data URL previews to keep payload small
                        if (key === 'dataUrl') {
                            delete obj[key];
                            continue;
                        }

                        // Remove only client-only fields.
                        // Preserve server-provided path fields (path, thumbPath) and
                        // server-generated preview fields (pathIncSASToken, thumbPathIncSASToken)
                        // so downstream UI and caching can use them. We still remove
                        // large client-side payloads below (dataUrl, file).

                        // If a string value looks like a data: URL, remove it
                        if (typeof val === 'string' && val.startsWith && val.startsWith('data:')) {
                            delete obj[key];
                            continue;
                        }

                        // Recurse into nested objects/arrays
                        if (typeof val === 'object' && val !== null) {
                            walk(val);
                        }
                    } catch (e) {
                        // ignore and continue
                    }
                }
            };

            walk(clone);
            return clone;
        } catch (e) {
            return t;
        }
    }

    create = tenant => {

        return polly()
            .logger(error => {
                console.error("An error occurred in updateUser", error); // will be hit 5 times if issues
            })
            .waitAndRetry(5)
            .executeForPromise(() => {
                return new Promise(async (resolve, reject) => { // eslint-disable-line no-unused-vars

                    tenant.rentDueDayOfMonth = 1;
                    this.#addTenantToCache(tenant);

                    if (this.inProgress !== InteractionStatus.None) {
                        try {
                            const token = await this.instance.acquireTokenSilent(this.accessTokenRequest);
                            return token;
                        } catch (error) {
                            console.error('Error acquiring token silently:', error);
                            throw error;
                        }
                    }

                    this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                        .then(accessTokenResponse => {

                            if (!!!accessTokenResponse) {
                                return reject("No access token");
                            }

                            const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                            const headers = new Headers();
                            headers.append("Authorization", bearer);
                            headers.append("Access-Token", accessTokenResponse.accessToken);
                            headers.append("Content-Type", "application/json");

                            const payload = this._stripClientIds(tenant);
                            const options = {
                                method: "POST",
                                headers: headers,
                                body: JSON.stringify(cleanUpJsonForTransport(payload))
                            };

                            fetch(`${this.baseUrl}/create`, options)
                                .then(response => response.json())
                                .then(data => {
                                    const result = cleanUpJsonForTransport(Object.assign({}, hydrateObjects(data)));
                                    TenantApi.clearCache(); // Invalidate cache after create
                                    this.#addTenantToCache(result);
                                    return resolve(result);
                                })
                                .catch(error => {
                                    console.log(error);
                                    return reject(error)
                                });
                        })
                        .catch((error) => {
                            if (error instanceof InteractionRequiredAuthError) {
                                // Let centralized auth handling manage redirects
                            }
                            console.log(error);
                            return reject(error)
                        });
                });
            });
    };

    getAll = () => {
        return polly()
            .logger(error => {
                console.error("An error occurred in getAll", error); // will be hit 5 times if issues
            })
            .waitAndRetry(5)
            .executeForPromise(() => {
                return new Promise(async (resolve, reject) => { // eslint-disable-line no-unused-vars

                    if (this.inProgress !== InteractionStatus.None) {
                        try {
                            const token = await this.instance.acquireTokenSilent(this.accessTokenRequest);
                            return token;
                        } catch (error) {
                            console.error('Error acquiring token silently:', error);
                            throw error;
                        }
                    }

                    this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                        .then(accessTokenResponse => {

                            if (!!!accessTokenResponse) {
                                return reject("No access token");
                            }

                            const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                            const headers = new Headers();
                            headers.append("Authorization", bearer);
                            headers.append("Content-Type", "application/json");

                            const url = `${this.baseUrl}/getAll`;

                            const options = {
                                method: "GET",
                                headers: headers
                            };

                            fetch(url, options)
                                .then(response => response.json())
                                .then(data => {
                                    const result = cleanUpJsonForTransport(Object.assign([], hydrateObjects(data)));

                                    result.forEach(t =>
                                        t.tenancies.forEach(t =>
                                            t.debitsAndCredits.forEach(dc => dc.id = new ObjectID().toHexString()
                                            ))
                                    );

                                    // Assign client-side tenancy ids for UI stability
                                    for (const tenant of result) {
                                        if (Array.isArray(tenant.tenancies)) {
                                            tenant.tenancies.forEach(ten => {
                                                if (!ten.clientId) ten.clientId = generateObjectId();
                                            });
                                        }
                                    }

                                    this.#addTenantsToCache(result);
                                    return resolve(result);
                                })
                                .catch(error => {
                                    console.log(error);
                                    return reject(error)
                                });
                        })
                        .catch((error) => {
                            if (error instanceof InteractionRequiredAuthError) {
                                // Let centralized auth handling manage redirects
                            }
                            console.log(error);
                            return reject(error)
                        });
                });
            });
    };

    list = (companyId, landlordOrOwnerId, propertyId) => {
        return polly()
            .logger(error => {
                console.error("An error occurred in list", error); // will be hit 5 times if issues
            })
            .waitAndRetry(5)
            .executeForPromise(() => {
                return new Promise(async (resolve, reject) => { // eslint-disable-line no-unused-vars

                    if (this.inProgress !== InteractionStatus.None) {
                        try {
                            const token = await this.instance.acquireTokenSilent(this.accessTokenRequest);
                            return token;
                        } catch (error) {
                            console.error('Error acquiring token silently:', error);
                            throw error;
                        }
                    }

                    this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                        .then(async accessTokenResponse => {

                            if (!!!accessTokenResponse) {
                                return reject("No access token");
                            }

                            const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                            const headers = new Headers();
                            headers.append("Authorization", bearer);
                            headers.append("Content-Type", "application/json");

                            const url = `${this.baseUrl}/list/${companyId}/${landlordOrOwnerId}/${propertyId}`;

                            const options = {
                                method: "GET",
                                headers: headers
                            };

                            try {
                                const response = await fetch(url, options);

                                // Check if the response was successful
                                if (!response.ok) {
                                    throw new Error(`HTTP error! status: ${response.status}`);
                                }

                                // Check if there's content to parse
                                const contentType = response.headers.get("content-type");
                                if (!contentType || !contentType.includes("application/json")) {
                                    throw new Error("Response was not JSON");
                                }

                                const text = await response.text();
                                if (!text) {
                                    return []; // or however you want to handle empty responses
                                }

                                const data = JSON.parse(text);

                                const result = cleanUpJsonForTransport(Object.assign([], hydrateObjects(data)));

                                // Update IDs using more readable nested iteration
                                for (const tenant of result) {
                                    for (const tenancy of tenant.tenancies) {
                                        for (const debitCredit of tenancy.debitsAndCredits) {
                                            debitCredit.id = new ObjectID().toHexString();
                                        }
                                    }
                                }

                                this.#addTenantsToCache(result);
                                return resolve(result);
                            } catch (error) {
                                console.error('API Error:', error);
                                throw error;
                            }
                        })
                        .catch((error) => {
                            if (error instanceof InteractionRequiredAuthError) {
                                // Let centralized auth handling manage redirects
                            }
                            console.log(error);
                            return reject(error)
                        });
                });
            });
    };

    get = tenantId => {
        return polly()
            .logger(error => {
                console.error("An error occurred in get", error); // will be hit 5 times if issues
            })
            .waitAndRetry(5)
            .executeForPromise(() => {
                return new Promise(async (resolve, reject) => { // eslint-disable-line no-unused-vars


                    const existingTenant = this.#getTenantFromCache(tenantId); 
                    if (existingTenant) {
                        return resolve(existingTenant); // found in cache
                    }

                    if (this.inProgress !== InteractionStatus.None) {
                        try {
                            const token = await this.instance.acquireTokenSilent(this.accessTokenRequest);
                            return token;
                        } catch (error) {
                            console.error('Error acquiring token silently:', error);
                            throw error;
                        }
                    }

                    this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                        .then(accessTokenResponse => {

                            if (!!!accessTokenResponse) {
                                return reject("No access token");
                            }

                            const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                            const headers = new Headers();
                            headers.append("Authorization", bearer);
                            headers.append("Content-Type", "application/json");

                            const url = `${this.baseUrl}/tenant/${tenantId}`;

                            const options = {
                                method: "GET",
                                headers: headers
                            };

                            fetch(url, options)
                                .then(response => response.json())
                                .then(data => {
                                    try { console.log('TenantApi.get: raw response', data); } catch(e) {}
                                    const result = cleanUpJsonForTransport(Object.assign({}, hydrateObjects(data)));

                                    result.tenancies.forEach(t =>
                                        t.debitsAndCredits.forEach(dc => dc.id = new ObjectID().toHexString()
                                        ));

                                    this.#addTenantToCache(result);
                                    try { console.log('TenantApi.get: hydrated result', result); } catch(e) {}
                                    return resolve(result);
                                })
                                .catch(error => {
                                    console.log(error);
                                    return reject(error)
                                });
                        })
                        .catch((error) => {
                            if (error instanceof InteractionRequiredAuthError) {
                                // Let centralized auth handling manage redirects
                            }
                            console.log(error);
                            return reject(error)
                        });
                });
            });
    };

    update = tenant => {

        return polly()
            .logger(error => {
                console.error("An error occurred in update", error); // will be hit 5 times if issues
            })
            .waitAndRetry(5)
            .executeForPromise(() => {
                return new Promise(async (resolve, reject) => { // eslint-disable-line no-unused-vars

                    if (tenant.xeroContact) {
                        tenant.xeroContactId = tenant.xeroContact.xeroContactId;
                    }

                    tenant.rentDueDayOfMonth ??= 1;

                    this.#addTenantToCache(tenant);

                    if (this.inProgress !== InteractionStatus.None) {
                        try {
                            const token = await this.instance.acquireTokenSilent(this.accessTokenRequest);
                            return token;
                        } catch (error) {
                            console.error('Error acquiring token silently:', error);
                            throw error;
                        }
                    }

                    this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                        .then(accessTokenResponse => {

                            if (!!!accessTokenResponse) {
                                return reject("No access token");
                            }

                            const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                            const headers = new Headers();
                            headers.append("Authorization", bearer);
                            headers.append("Access-Token", accessTokenResponse.accessToken);
                            headers.append("Content-Type", "application/json");

                            const payload = this._stripClientIds(tenant);
                            try {
                                const payloadSize = JSON.stringify(payload).length;
                                console.debug('TenantApi.update: payload size (bytes)', payloadSize);
                            } catch (e) {
                                console.debug('TenantApi.update: could not compute payload size');
                            }
                            const options = {
                                method: "PATCH",
                                headers: headers,
                                body: JSON.stringify(cleanUpJsonForTransport(payload))
                            };

                            fetch(`${this.baseUrl}/update`, options)
                                .then(response => response.json())
                                .then(data => {
                                    try {
                                        console.log('TenantApi.update: raw response', data);
                                    } catch (e) {}
                                    const result = cleanUpJsonForTransport(Object.assign({}, hydrateObjects(data)));
                                    try {
                                        console.log('TenantApi.update: hydrated result', result);
                                    } catch (e) {}
                                    TenantApi.clearCache(); // Invalidate cache after update
                                    this.#addTenantToCache(result);
                                    return resolve(result);
                                })
                                .catch(error => {
                                    console.log(error);
                                    return reject(error)
                                });
                        })
                        .catch((error) => {
                            if (error instanceof InteractionRequiredAuthError) {
                                // Let centralized auth handling manage redirects
                            }
                            console.log(error);
                            return reject(error)
                        });
                });
            });
    };

    getRentReport = () => {
        return polly()
            .logger(error => {
                console.error("An error occurred in get", error); // will be hit 5 times if issues
            })
            .waitAndRetry(5)
            .executeForPromise(() => {
                return new Promise(async (resolve, reject) => { // eslint-disable-line no-unused-vars

                    if (this.inProgress !== InteractionStatus.None) {
                        try {
                            const token = await this.instance.acquireTokenSilent(this.accessTokenRequest);
                            return token;
                        } catch (error) {
                            console.error('Error acquiring token silently:', error);
                            throw error;
                        }
                    }

                    this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                        .then(accessTokenResponse => {

                            if (!!!accessTokenResponse) {
                                return reject("No access token");
                            }

                            const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                            const headers = new Headers();
                            headers.append("Authorization", bearer);
                            headers.append("Content-Type", "application/json");

                            const url = `${this.baseUrl}/GetRentReport`;

                            const options = {
                                method: "GET",
                                headers: headers
                            };

                            fetch(url, options)
                                .then(response => response.json())
                                .then(data => {
                                    const result = cleanUpJsonForTransport(Object.assign({}, hydrateObjects(data)));
                                    //this.#addTenantToCache(result);
                                    return resolve(result);
                                })
                                .catch(error => {
                                    console.log(error);
                                    return reject(error)
                                });
                        })
                        .catch((error) => {
                            if (error instanceof InteractionRequiredAuthError) {
                                // Let centralized auth handling manage redirects
                            }
                            console.log(error);
                            return reject(error)
                        });
                });
            });
    };

    saveRentTrackerDTO = rentTrackerDto => {

        return polly()
            .logger(error => {
                console.error("An error occurred in saveRentTrackerDTO", error); // will be hit 5 times if issues
            })
            .waitAndRetry(5)
            .executeForPromise(() => {
                return new Promise(async (resolve, reject) => { // eslint-disable-line no-unused-vars

                    if (this.inProgress !== InteractionStatus.None) {
                        try {
                            const token = await this.instance.acquireTokenSilent(this.accessTokenRequest);
                            return token;
                        } catch (error) {
                            console.error('Error acquiring token silently:', error);
                            throw error;
                        }
                    }

                    this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                        .then(accessTokenResponse => {

                            if (!!!accessTokenResponse) {
                                return reject("No access token");
                            }

                            const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                            const headers = new Headers();
                            headers.append("Authorization", bearer);
                            headers.append("Access-Token", accessTokenResponse.accessToken);
                            headers.append("Content-Type", "application/json");

                            const options = {
                                method: "POST",
                                headers: headers,
                                body: JSON.stringify(cleanUpJsonForTransport(rentTrackerDto))
                            };

                            fetch(`${this.baseUrl}/saveMonthLine`, options)
                                .then(response => response.json())
                                .then(data => {
                                    const result = cleanUpJsonForTransport(Object.assign([], hydrateObjects(data)));
                                    //this.#addTenantToCache(result);
                                    return resolve(result);
                                })
                                .catch(error => {
                                    console.log(error);
                                    return reject(error)
                                });
                        })
                        .catch((error) => {
                            if (error instanceof InteractionRequiredAuthError) {
                                // Let centralized auth handling manage redirects
                            }
                            console.log(error);
                            return reject(error)
                        });
                });
            });
    };


    getOverallBalance = () => {
        // Create a variable to store auth errors that shouldn't be retried
        let authError = null;

        return polly()
            .logger(error => {
                console.error("An error occurred in getOverallBalance", error);
            })
            .handle(err => {
                // If we've already detected an auth error, don't retry
                if (authError) return false;

                // Don't retry for 401/403 errors
                if (err.status === 401 || err.status === 403) {
                    authError = err; // Store the auth error
                    console.log(`Authorization error (${err.status}), not retrying`);
                    return false; // Don't retry
                }

                return true; // Retry for other errors
            })
            .waitAndRetry(5)
            .executeForPromise(() => {
                return new Promise(async (resolve, reject) => {
                    // If we already have an auth error from a previous attempt, reject immediately
                    if (authError) {
                        return reject(authError);
                    }

                    if (this.inProgress !== InteractionStatus.None) {
                        try {
                            const token = await this.instance.acquireTokenSilent(this.accessTokenRequest);
                            return token;
                        } catch (error) {
                            console.error('Error acquiring token silently:', error);
                            throw error;
                        }
                    }

                    this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                        .then(accessTokenResponse => {
                            if (!!!accessTokenResponse) {
                                return reject("No access token");
                            }

                            const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                            const headers = new Headers();
                            headers.append("Authorization", bearer);
                            headers.append("Content-Type", "application/json");

                            const url = `${this.baseUrl}/dashboard/tenantsBalance`;

                            const options = {
                                method: "GET",
                                headers: headers
                            };

                            fetch(url, options)
                                .then(response => {
                                    // First check if the response is ok (status in the range 200-299)
                                    if (!response.ok) {
                                        // Create an error object with status information
                                        const error = new Error(`HTTP Error: ${response.status} ${response.statusText}`);
                                        error.status = response.status;
                                        error.statusText = response.statusText;
                                        error.response = response;
                                        throw error; // This will be caught by the .catch() below
                                    }
                                    return response.json(); // Only try to parse JSON for successful responses
                                })
                                .then(data => {
                                    const result = cleanUpJsonForTransport(Object.assign({}, hydrateObjects(data)));
                                    return resolve(result);
                                })
                                .catch(error => {
                                    console.log(error);
                                    // If this is an HTTP error we created above, we can access the status
                                    if (error.status) {
                                        console.log(`API returned status code: ${error.status}`);
                                        // Store auth errors so we don't retry
                                        if (error.status === 401 || error.status === 403) {
                                            authError = error;
                                            console.log("Authorization error, not retrying");
                                        }
                                    }
                                    return reject(error);
                                });
                        })
                        .catch((error) => {
                            if (error instanceof InteractionRequiredAuthError) {
                                // Let centralized auth handling manage redirects
                            }
                            console.log(error);
                            return reject(error);
                        });
                });
            });
    };

    addBulkDebitCredit = data => {
        return polly()
            .logger(error => {
                console.error("An error occurred in addBulkDebitCredit", error); // will be hit 5 times if issues
            })
            .waitAndRetry(5)
            .executeForPromise(() => {
                return new Promise(async (resolve, reject) => { // eslint-disable-line no-unused-vars

                    if (this.inProgress !== InteractionStatus.None) {
                        try {
                            const token = await this.instance.acquireTokenSilent(this.accessTokenRequest);
                            return token;
                        } catch (error) {
                            console.error('Error acquiring token silently:', error);
                            throw error;
                        }
                    }

                    this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                        .then(async accessTokenResponse => {

                            if (!!!accessTokenResponse) {
                                return reject("No access token");
                            }

                            const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                            const headers = new Headers();
                            headers.append("Authorization", bearer);
                            headers.append("Access-Token", accessTokenResponse.accessToken);
                            headers.append("Content-Type", "application/json");

                            const options = {
                                method: "PATCH",
                                headers: headers,
                                body: JSON.stringify(cleanUpJsonForTransport(data))
                            };

                            try {
                                const response = await fetch(`${this.baseUrl}/addBulkDebitAndCredit`, options);
                                const data = await response.json();
                                const result = cleanUpJsonForTransport(Object.assign([], hydrateObjects(data)));
                                TenantApi.clearCache(); // Invalidate cache after bulk update
                                this.#addTenantsToCache(result);
                                return resolve(result);
                            } catch (error) {
                                console.log(error);
                                return reject(error);
                            }
                        })
                        .catch((error) => {
                            if (error instanceof InteractionRequiredAuthError) {
                                // Let centralized auth handling manage redirects
                            }
                            console.log(error);
                            return reject(error)
                        });
                });
            });
    };

};